import { useQuery } from '@tanstack/react-query'

import { api } from './api'
import type { AvailabilitySettings } from './optypes'
import { DEFAULT_TIMEZONE } from './optypes'
import type { Category } from './types'

/**
 * The operator's configured timezone.
 *
 * Every operator-facing time must be formatted in this, not in the browser's zone — see the
 * note on `shortDateTime`. Cached for the session: it changes about once a decade, and a
 * per-screen fetch would be silly.
 */
export function useOperatorTimezone(): string {
  const { data } = useQuery({
    queryKey: ['op-av-settings'],
    queryFn: () => api.get<AvailabilitySettings>('/op/availability/settings'),
    staleTime: 60 * 60_000,
  })
  return data?.timezone ?? DEFAULT_TIMEZONE
}

/**
 * Catalog id -> human name, for the operator console.
 *
 * Andrew should read "Bathrooms · Trim & Finish Carpentry", not "bathroom · trim". The ids are
 * ours; the names are his.
 */
export function useCategoryNames(): (ids: string[]) => string {
  const { data } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/catalog/categories'),
    staleTime: 60 * 60_000,
  })
  const byId = new Map((data ?? []).map((c) => [c.id, c.name]))
  // Falls back to the raw id rather than hiding a category the catalog no longer knows.
  return (ids: string[]) => ids.map((id) => byId.get(id) ?? id).join(' · ')
}
